import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, X } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'

import { useJobPanelData } from '../hooks/use-job-panel-data'
import {
  dayStatusOf,
  openJobsForWorker,
  problemJobsForWorker,
  rankCandidates,
  type Candidate,
  type ScheduleWorker,
} from '../utils/eligibility'
import { resolvePanelAssignments, resolveReadOnlyReason, type PanelAssignment } from '../utils/job-assignments'
import { isUnassignedRow, type ScheduleJobRow } from '../utils/job-rows'
import { issueOf, type ScheduleBoard } from '../utils/schedule-board'
import type { ScheduleSelection } from '../utils/selection'
import { CategoryPill, JobRef, PanelHeading, WorkerAvatar } from './ScheduleBits'
import { BRAND, describeDay, ISSUE_TONE, plural } from './schedule-styles'

/** Worker categories: Assign waits while they load; when the read failed the category rule is skipped. */
export type CategoryState = 'loading' | 'ready' | 'failed'

const CATEGORY_NOTE: Record<Exclude<CategoryState, 'ready'>, string> = {
  loading: 'Loading worker categories…',
  failed: "Couldn't load worker categories, so categories are not checked.",
}

export interface PanelActions {
  /** Adds `worker` to the job (fills an open slot, else a new assignment). */
  onAssign: (job: ScheduleJobRow, worker: ScheduleWorker) => Promise<boolean>
  /** Adds `worker`, then removes `previous` (their assignment `assignmentId`). */
  onReplace: (job: ScheduleJobRow, worker: ScheduleWorker, previous: ScheduleWorker, assignmentId: string) => Promise<boolean>
  onRemove: (job: ScheduleJobRow, worker: ScheduleWorker, assignmentId: string) => Promise<boolean>
}

const whenOf = (job: ScheduleJobRow) => {
  const info = describeDay(job.date)
  return `${info.dow} ${info.num} · ${job.timeLabel}`
}

const primaryButton = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[10px] px-4 text-sm font-semibold text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-2'
const secondaryButton = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[10px] border border-[#C9D3C8] bg-white px-3.5 text-sm font-semibold text-[#14231B] hover:bg-[#F4F9F5] disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-2'

const Spinner = () => <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />

function QueueView({ board, onPickJob }: { board: ScheduleBoard; onPickJob: (job: ScheduleJobRow) => void }) {
  return (
    <>
      <p className="px-[18px] pb-1 pt-3.5 text-sm leading-[1.45] text-[#3C4B43]">
        Click a job to see who is free for it. Click a worker name to see which open jobs fit them.
      </p>
      {board.attention.length === 0 ? (
        <p className="px-[18px] py-6 text-[15px] font-semibold text-[#14532D]">All clear. Every job has a worker who can do it.</p>
      ) : (
        <ul>
          {board.attention.map(({ job, issue }) => {
            const tone = ISSUE_TONE[issue.kind]
            return (
              <li key={job.key} className="flex flex-col gap-2 border-b border-[#EDF1EC] px-[18px] py-3.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-bold tabular-nums">{whenOf(job)}</span>
                  <span className="rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ background: tone.bg, color: tone.fg }}>{tone.label}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-bold">{job.title}</span>
                  <CategoryPill name={job.categoryLabel} />
                </div>
                <JobRef jobRef={job.jobRef} />
                <p className="text-sm text-[#3C4B43]">{issue.text}</p>
                <button
                  type="button"
                  onClick={() => onPickJob(job)}
                  className={cn(primaryButton, 'self-start')}
                  style={{ background: BRAND }}
                  aria-label={`Find a free worker for ${job.title}, ${whenOf(job)}`}
                >
                  Find a free worker
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

const openJobLink = 'inline-flex min-h-11 items-center px-1 text-sm font-semibold text-[#14532D] underline underline-offset-2'

/** Title + category, job ref, day / time and location of the selected job. */
function JobCardHeader({ job }: { job: ScheduleJobRow }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xl font-bold">{job.title}</span>
        <CategoryPill name={job.categoryLabel} />
      </div>
      <JobRef jobRef={job.jobRef} />
      <p className="text-sm font-semibold tabular-nums">{describeDay(job.date).long} · {job.timeLabel}</p>
      {job.locationLabel ? <p className="text-sm text-[#3C4B43]">{job.locationLabel}</p> : null}
    </>
  )
}

/** Completed jobs are read only: details and status only, no worker options. */
function CompletedJobView({ job, serviceInstanceId }: { job: ScheduleJobRow; serviceInstanceId: string | null }) {
  const doneBy = job.workers.length > 0 ? `Done by ${job.workers.map((worker) => worker.name).join(', ')}. ` : ''
  return (
    <div className="flex flex-col gap-2 px-[18px] py-4">
      <JobCardHeader job={job} />
      <p className="rounded-[10px] bg-[#DDF1E3] px-3 py-2 text-sm font-semibold text-[#14532D]" role="status">Job completed</p>
      <p className="text-sm text-[#3C4B43]">{doneBy}Completed jobs are read only, so no worker can be assigned.</p>
      <Link to={withServiceInstanceId(`/jobs/${job.jobId}`, serviceInstanceId)} className={cn(openJobLink, 'self-start')}>
        Open job
      </Link>
    </div>
  )
}

function JobView({
  board,
  job,
  isPending,
  categoriesLoading,
  serviceInstanceId,
  actions,
}: {
  board: ScheduleBoard
  job: ScheduleJobRow
  isPending: boolean
  categoriesLoading: boolean
  serviceInstanceId: string | null
  actions: PanelActions
}) {
  const { job: detail, assignments } = useJobPanelData(job.jobId)
  const [busyWorkerId, setBusyWorkerId] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<{ worker: ScheduleWorker; assignment: PanelAssignment } | null>(null)

  const issue = issueOf(board, job)
  const readOnlyReason = resolveReadOnlyReason(detail.data)
  const panelAssignments = useMemo(() => (assignments.data ? resolvePanelAssignments(assignments.data) : null), [assignments.data])
  const { free, blocked } = useMemo(() => rankCandidates(job, board.workers, board.allJobs, board.check), [board, job])

  // The worker Replace / Remove act on: the first one with a problem, else the first assigned.
  const target = useMemo(() => {
    const problemWorker = issue?.problems[0]?.worker
    const assigned = problemWorker ?? (job.workers[0] ? board.workerById.get(job.workers[0].workerId) : undefined)
    if (!assigned) return null
    const assignment = panelAssignments?.find((item) => item.workerId === assigned.id) ?? null
    return { worker: assigned, assignment }
  }, [board.workerById, issue, job.workers, panelAssignments])

  const replaces = Boolean(target) && !isUnassignedRow(job)
  const locked = isPending || detail.isPending || Boolean(readOnlyReason)
  // Until the job's assignments load it is unknown whether Replace is possible: actions wait.
  const waitingForAssignments = replaces && assignments.isPending
  const replaceBlocked = replaces && (!target?.assignment || !target.assignment.canRemove)
  const replaceBlockedText = replaces && target && panelAssignments
    ? (target.assignment
      ? target.assignment.removeBlockedReason && `${target.worker.name}: ${target.assignment.removeBlockedReason}, so they can't be replaced here. Assign adds a worker instead.`
      : `${target.worker.name}'s assignment was not found, so they can't be replaced here. Assign adds a worker instead.`)
    : null

  let status = { text: 'Needs a worker. Pick someone free below.', bg: '#FFF4D6', fg: '#5E3C00' }
  if (issue && issue.problems.length > 0) status = { text: `Problem: ${issue.text}`, bg: '#FADBD8', fg: '#8A1C12' }
  else if (issue) status = { ...status, text: `${issue.text} Pick someone free below.` }
  else if (job.workers.length > 0) status = { text: `Assigned to ${job.workers.map((worker) => worker.name).join(', ')}. No problems.`, bg: '#DDF1E3', fg: '#14532D' }

  const run = async (workerId: string, action: () => Promise<boolean>) => {
    setBusyWorkerId(workerId)
    await action()
    setBusyWorkerId(null)
  }

  const pick = (candidate: Candidate) => {
    if (replaces && target?.assignment && !replaceBlocked) {
      void run(candidate.worker.id, () => actions.onReplace(job, candidate.worker, target.worker, target.assignment!.assignmentId))
    } else {
      void run(candidate.worker.id, () => actions.onAssign(job, candidate.worker))
    }
  }

  const doRemove = async () => {
    const pending = confirmRemove
    setConfirmRemove(null)
    if (!pending) return
    await run(pending.worker.id, () => actions.onRemove(job, pending.worker, pending.assignment.assignmentId))
  }

  // Older overview without job_status: the job detail still makes a completed job read only.
  if (detail.data?.status === 'completed') {
    return <CompletedJobView job={job} serviceInstanceId={serviceInstanceId} />
  }

  const freeSub = (candidate: Candidate) => {
    const status = dayStatusOf(candidate.worker, job.date)
    const load = candidate.load ? `${plural(candidate.load, 'other job')} that day` : 'No other jobs that day'
    return `${load} · ${status.kind === 'shift' ? status.label : 'Hours not set'}`
  }

  return (
    <>
      <div className="flex flex-col gap-2 border-b border-[#EDF1EC] px-[18px] py-4">
        <JobCardHeader job={job} />
        <p className="rounded-[10px] px-3 py-2 text-sm font-semibold" style={{ background: status.bg, color: status.fg }} role="status">{status.text}</p>
        {readOnlyReason ? <p className="rounded-[10px] bg-[#F1F3F2] px-3 py-2 text-sm font-medium text-[#3C4B43]">{readOnlyReason}</p> : null}
        {assignments.isError ? (
          <p className="text-sm font-semibold text-[#8A1C12]" role="alert">
            Couldn&apos;t load this job&apos;s assignments.{' '}
            <button type="button" className="underline underline-offset-2" onClick={() => void assignments.refetch()}>Try again</button>
          </p>
        ) : null}
        {replaceBlockedText && !readOnlyReason ? <p className="text-[13px] text-[#6B3A00]">{replaceBlockedText}</p> : null}
        <div className="flex flex-wrap items-center gap-2">
          {target && !readOnlyReason ? (
            <button
              type="button"
              className={secondaryButton}
              disabled={locked || !target.assignment?.canRemove}
              onClick={() => target.assignment && setConfirmRemove({ worker: target.worker, assignment: target.assignment })}
              aria-label={`Remove ${target.worker.name} from ${job.title}`}
              title={target.assignment?.removeBlockedReason ?? undefined}
            >
              {busyWorkerId === target.worker.id && isPending ? <Spinner /> : null}
              Remove worker
            </button>
          ) : null}
          <Link to={withServiceInstanceId(`/jobs/${job.jobId}`, serviceInstanceId)} className={openJobLink}>
            Open job
          </Link>
        </div>
      </div>

      <PanelHeading tone="green">{`Free for this job (${free.length})`}</PanelHeading>
      {free.length === 0 ? (
        <p className="px-[18px] pb-4 pt-2 text-sm font-semibold text-[#5E3C00]">Nobody is free at this time. Try moving the job or check the list below.</p>
      ) : (
        <ul>
          {free.map((candidate) => {
            const firstName = candidate.worker.name.split(' ')[0]
            const label = candidate.isCurrent ? 'Assigned' : replaces && !replaceBlocked ? `Replace with ${firstName}` : 'Assign'
            const ariaLabel = candidate.isCurrent
              ? `${candidate.worker.name} is assigned`
              : replaces && !replaceBlocked && target
                ? `Replace ${target.worker.name} with ${candidate.worker.name} on ${job.title}`
                : `Assign ${candidate.worker.name} to ${job.title}`
            return (
              <li key={candidate.worker.id} className="flex items-center gap-2.5 border-b border-[#EDF1EC] px-[18px] py-2.5">
                <WorkerAvatar name={candidate.worker.name} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{candidate.worker.name}</span>
                  <span className="block text-[13px] text-[#3C4B43]">{freeSub(candidate)}</span>
                </span>
                {candidate.isCurrent ? (
                  <span className="inline-flex min-h-11 items-center rounded-[10px] bg-[#DDF1E3] px-3.5 text-sm font-semibold text-[#14532D]">Assigned</span>
                ) : (
                  <button
                    type="button"
                    className={primaryButton}
                    style={{ background: BRAND }}
                    disabled={locked || waitingForAssignments || categoriesLoading}
                    onClick={() => pick(candidate)}
                    aria-label={ariaLabel}
                  >
                    {busyWorkerId === candidate.worker.id && isPending ? <Spinner /> : null}
                    {label}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <PanelHeading>{`Can't take it (${blocked.length})`}</PanelHeading>
      <ul>
        {blocked.map((candidate) => (
          <li key={candidate.worker.id} className="flex items-center gap-2.5 border-b border-[#EDF1EC] px-[18px] py-2.5">
            <WorkerAvatar name={candidate.worker.name} dim />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">{candidate.worker.name}</span>
              <span className="block text-[13px] font-semibold" style={{ color: ISSUE_TONE[candidate.result.kind as keyof typeof ISSUE_TONE]?.fg }}>
                {candidate.result.text}
              </span>
            </span>
            {candidate.isCurrent ? <span className="rounded-full bg-[#FADBD8] px-2.5 py-0.5 text-xs font-bold text-[#8A1C12]">Assigned now</span> : null}
          </li>
        ))}
      </ul>

      <AlertDialog open={confirmRemove != null} onOpenChange={(open) => { if (!open) setConfirmRemove(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmRemove?.worker.name} from this job?</AlertDialogTitle>
            <AlertDialogDescription>
              Their assignment on {job.title} is deleted and its slot is removed too, so the job needs one fewer worker.
              Their work-day entries for this job are cancelled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void doRemove()} className="bg-[#cb3d4d] hover:bg-[#b3303f]">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function WorkerView({
  board,
  worker,
  scopeDays,
  isDayScope,
  pendingRowKeys,
  categoriesLoading,
  onPickJob,
  onAssign,
}: {
  board: ScheduleBoard
  worker: ScheduleWorker
  scopeDays: string[]
  isDayScope: boolean
  pendingRowKeys: ReadonlySet<string>
  categoriesLoading: boolean
  onPickJob: (job: ScheduleJobRow) => void
  onAssign: PanelActions['onAssign']
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const { fits, noFit } = useMemo(() => openJobsForWorker(worker, board.jobs, scopeDays, board.check), [board, scopeDays, worker])
  const issues = useMemo(() => problemJobsForWorker(worker, board.jobs, scopeDays, board.check), [board, scopeDays, worker])

  const statuses = board.days.map((day) => ({ day, status: dayStatusOf(worker, day) }))
  const shiftLabels = Array.from(new Set(statuses.flatMap(({ status }) => (status.kind === 'shift' ? [status.label] : []))))
  const notWorking = statuses
    .filter(({ status }) => status.kind === 'leave' || status.kind === 'off')
    .map(({ day, status }) => `${describeDay(day).dow}${status.kind === 'leave' ? ' (leave)' : ''}`)
  const shiftText = [
    shiftLabels.length === 1 ? `Shift ${shiftLabels[0]}` : shiftLabels.length > 1 ? 'Shift hours vary by day' : 'No shift hours saved this week',
    notWorking.length ? `Not working: ${notWorking.join(', ')}` : null,
  ].filter(Boolean).join(' · ')

  const categories = worker.anyCategory ? ['Any category', ...worker.categoryNames] : worker.categoryNames
  const scopeText = isDayScope ? 'on this day' : 'this week'

  const assign = async (job: ScheduleJobRow) => {
    setBusyKey(job.key)
    await onAssign(job, worker)
    setBusyKey(null)
  }

  return (
    <>
      <div className="flex flex-col gap-2.5 border-b border-[#EDF1EC] px-[18px] py-4">
        <div className="flex items-center gap-3">
          <WorkerAvatar name={worker.name} size="lg" />
          <span className="min-w-0 flex-1">
            <span className="block text-xl font-bold">{worker.name}</span>
            <span className="block text-[13px] text-[#3C4B43]">{shiftText}</span>
          </span>
          <Link
            to={`/workers/${worker.id}`}
            className="inline-flex min-h-11 flex-none items-center px-1 text-sm font-semibold text-[#14532D] underline underline-offset-2"
          >
            View profile
          </Link>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {!worker.categoriesKnown ? (
            <span className="text-[13px] text-[#4A5A51]">Categories not loaded</span>
          ) : categories.length === 0 ? (
            <span className="text-[13px] text-[#4A5A51]">No categories</span>
          ) : (
            categories.map((name) => <CategoryPill key={name} name={name} />)
          )}
        </div>
        <ul className="grid grid-cols-7 gap-1" aria-label={`${worker.name}'s week`}>
          {statuses.map(({ day, status }) => {
            const info = describeDay(day)
            const cell = status.kind === 'shift'
              ? { text: 'On', bg: '#DDF1E3', fg: '#14532D' }
              : status.kind === 'leave'
                ? { text: 'Leave', bg: '#EBE3FA', fg: '#42237A' }
                : status.kind === 'off'
                  ? { text: 'Off', bg: '#E4E9E3', fg: '#2F3E36' }
                  : { text: 'Not set', bg: '#F4F6F3', fg: '#3C4B43' }
            return (
              <li key={day} className="rounded-lg py-1.5 text-center" style={{ background: cell.bg, color: cell.fg }} title={status.kind === 'shift' ? status.label : undefined}>
                <span className="block text-xs font-semibold">{info.dow}</span>
                <span className="block text-xs font-bold leading-tight">{cell.text}</span>
              </li>
            )
          })}
        </ul>
      </div>

      {issues.length > 0 ? (
        <>
          <PanelHeading tone="red">{`Their jobs with problems (${issues.length})`}</PanelHeading>
          <ul>
            {issues.map(({ job, result }) => (
              <li key={job.key} className="flex flex-col gap-1.5 border-b border-[#EDF1EC] px-[18px] py-2.5">
                <p className="text-sm font-bold">{whenOf(job)} · {job.title}</p>
                <JobRef jobRef={job.jobRef} />
                <p className="text-[13px] font-semibold text-[#8A1C12]">{result.text}</p>
                <button type="button" className={cn(secondaryButton, 'self-start')} onClick={() => onPickJob(job)} aria-label={`Find someone else for ${job.title}, ${whenOf(job)}`}>
                  Find someone else
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <PanelHeading tone="green">{`Open jobs they can take (${fits.length})`}</PanelHeading>
      {fits.length === 0 ? (
        <p className="px-[18px] pb-4 pt-2 text-sm text-[#3C4B43]">No open job {scopeText} fits {worker.name}.</p>
      ) : (
        <ul>
          {fits.map(({ job }) => (
            <li key={job.key} className="flex flex-col gap-1.5 border-b border-[#EDF1EC] px-[18px] py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-bold tabular-nums">{whenOf(job)}</span>
                <CategoryPill name={job.categoryLabel} />
              </div>
              <p className="text-base font-bold">{job.title}</p>
              <JobRef jobRef={job.jobRef} />
              <button
                type="button"
                className={cn(primaryButton, 'self-start')}
                style={{ background: BRAND }}
                disabled={pendingRowKeys.has(job.key) || categoriesLoading}
                onClick={() => void assign(job)}
              >
                {busyKey === job.key && pendingRowKeys.has(job.key) ? <Spinner /> : null}
                Assign to {worker.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {noFit.length > 0 ? (
        <>
          <PanelHeading>{`Open jobs that don't fit (${noFit.length})`}</PanelHeading>
          <ul>
            {noFit.map(({ job, result }) => (
              <li key={job.key} className="flex flex-col gap-0.5 border-b border-[#EDF1EC] px-[18px] py-2.5">
                <p className="text-sm font-semibold">{whenOf(job)} · {job.title}</p>
                <JobRef jobRef={job.jobRef} />
                <p className="text-[13px] font-semibold" style={{ color: ISSUE_TONE[result.kind as keyof typeof ISSUE_TONE]?.fg }}>{result.text}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  )
}

/** Sticky side panel: Needs attention (nothing selected), Find a worker (job) or the worker's fits. */
export function SchedulePanel({
  board,
  selection,
  view,
  selectedDay,
  note,
  pendingRowKeys,
  categoryState,
  serviceInstanceId,
  actions,
  onPickJob,
  onClear,
}: {
  board: ScheduleBoard
  selection: ScheduleSelection
  view: 'week' | 'day'
  selectedDay: string
  note: string | null
  pendingRowKeys: ReadonlySet<string>
  categoryState: CategoryState
  serviceInstanceId: string | null
  actions: PanelActions
  onPickJob: (job: ScheduleJobRow) => void
  onClear: () => void
}) {
  const job = selection?.type === 'job' ? board.jobByKey.get(selection.key) ?? null : null
  const worker = !job && selection?.type === 'worker' ? board.workerById.get(selection.id) ?? null : null
  const scopeDays = useMemo(() => (view === 'day' ? [selectedDay] : board.days), [board.days, selectedDay, view])
  const title = job ? (job.isCompleted ? 'Job details' : 'Find a worker') : worker ? worker.name : `Needs attention (${board.attention.length})`

  return (
    <aside
      className="min-w-0 overflow-hidden rounded-2xl border border-[#DDE4DC] bg-white xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto"
      aria-label="Scheduling panel"
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#E6ECE5] px-[18px] py-3.5">
        <h2 className="text-[17px] font-bold">{title}</h2>
        {job || worker ? (
          <button type="button" onClick={onClear} className={cn(secondaryButton, 'min-h-10 px-3 text-[13px]')} aria-label="Clear selection">
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Clear
          </button>
        ) : null}
      </div>

      {note ? <p className="bg-[#DDF1E3] px-[18px] py-2.5 text-sm font-semibold text-[#14532D]" role="status">{note}</p> : null}
      {((job && !job.isCompleted) || worker) && categoryState !== 'ready' ? (
        <p className="bg-[#F4F6F3] px-[18px] py-2 text-[13px] font-medium text-[#3C4B43]" role="status">{CATEGORY_NOTE[categoryState]}</p>
      ) : null}

      {job?.isCompleted ? (
        <CompletedJobView key={job.key} job={job} serviceInstanceId={serviceInstanceId} />
      ) : job ? (
        <JobView
          key={job.key}
          board={board}
          job={job}
          isPending={pendingRowKeys.has(job.key)}
          categoriesLoading={categoryState === 'loading'}
          serviceInstanceId={serviceInstanceId}
          actions={actions}
        />
      ) : worker ? (
        <WorkerView
          key={worker.id}
          board={board}
          worker={worker}
          scopeDays={scopeDays}
          isDayScope={view === 'day'}
          pendingRowKeys={pendingRowKeys}
          categoriesLoading={categoryState === 'loading'}
          onPickJob={onPickJob}
          onAssign={actions.onAssign}
        />
      ) : (
        <QueueView board={board} onPickJob={onPickJob} />
      )}
    </aside>
  )
}
